import { useCallback } from 'react';
import { useApolloClient, type ApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import type { RecipientUser } from '~/components/messages/RecipientPicker';
import { DIRECT_THREAD_WITH_QUERY } from '~/graphql/direct-messages';
import { useMessagingDockOptional } from '~/context/messagingDock';
import { stashComposeDraft } from '~/utils/composeDraftHandoff';

/**
 * Look up the id of an existing 1:1 thread with `userId`, or null. Never throws
 * — a lookup failure falls back to composing a new message.
 */
export async function lookupDirectThreadId(client: ApolloClient<object>, userId: string): Promise<string | null> {
  try {
    const { data } = await client.query({
      query: DIRECT_THREAD_WITH_QUERY,
      variables: { userId },
      fetchPolicy: 'network-only',
    });
    const id = data?.directThreadWith?.id;
    return id ? String(id) : null;
  } catch {
    return null;
  }
}

interface OpenDirectMessageOptions {
  /** Seed compose To without waiting on the messageableUsers cache. */
  recipient?: RecipientUser;
  /** Prefill body (opener text) when composing a new message. */
  body?: string;
  source?: string;
}

/**
 * Open a DM with a specific user the right way: reopen the existing thread if one
 * exists, otherwise open a fresh compose pre-addressed to them. Uses the dock
 * when it's mounted and falls back to the full-page /messages routes otherwise.
 * Every "Message" button should call this instead of opening compose directly, so
 * users never get a blank compose for a conversation they already have.
 */
export function useOpenDirectMessage() {
  const client = useApolloClient();
  const dock = useMessagingDockOptional();
  const navigate = useNavigate();

  return useCallback(
    async (userId: string, opts?: OpenDirectMessageOptions) => {
      const threadId = await lookupDirectThreadId(client, userId);

      if (dock) {
        if (threadId) {
          dock.openThread(threadId, { source: opts?.source });
        } else {
          dock.openCompose({
            recipientAsfId: userId,
            recipient: opts?.recipient,
            body: opts?.body,
            source: opts?.source,
          });
        }
        return;
      }

      // No dock mounted → the full-page routes.
      if (threadId) {
        navigate(`/messages/${threadId}`);
        return;
      }
      // Never put draft text in the URL — analytics / history / referrers.
      if (opts?.body) stashComposeDraft(opts.body);
      const params = new URLSearchParams({ to: userId });
      if (opts?.recipient?.displayName) params.set('name', opts.recipient.displayName);
      navigate(`/messages/new?${params.toString()}`);
    },
    [client, dock, navigate]
  );
}
